import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Printer } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Field, Sel } from "@/components/kit";
import { api, fmtErr, peso, dt, dur, METHODS, label } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isOffline, enqueue } from "@/lib/offline";

export function FeeLines({ fee }) {
  if (!fee) return null;
  return (
    <div className="space-y-1.5 text-sm" data-testid="fee-breakdown">
      <div className="flex justify-between text-muted-foreground"><span>{fee.plan_name}</span><span className="font-mono">{dur(fee.minutes)}</span></div>
      {fee.lines.map((l, i) => (
        <div key={i} className="flex justify-between"><span>{l.label}</span><span className="font-mono">{peso(l.amount)}</span></div>
      ))}
      <div className="flex justify-between border-t pt-1.5 font-semibold"><span>Total</span><span className="font-mono" data-testid="fee-total">{peso(fee.total)}</span></div>
      <div className="flex justify-between text-muted-foreground"><span>Paid</span><span className="font-mono">{peso(fee.amount_paid)}</span></div>
      <div className="flex justify-between text-base font-bold text-amber-500"><span>Balance due</span><span className="font-mono" data-testid="fee-balance">{peso(fee.balance)}</span></div>
    </div>
  );
}

export function PayDialog({ session, fee, open, onOpenChange, onPaid }) {
  const { can } = useAuth();
  const [method, setMethod] = useState("gcash");
  const [reason, setReason] = useState("");
  const [fail, setFail] = useState(false);
  const [busy, setBusy] = useState(false);
  const methods = METHODS.filter((m) => m.value !== "cash" || can("override", "approve"));
  const pay = async () => {
    setBusy(true);
    const payload = { session_id: session.id, method, cash_reason: reason || null, simulate_failure: fail };
    try {
      if (isOffline()) {
        const it = enqueue("payment", payload);
        toast.warning(`OFFLINE: payment queued (${it.client_ref}). Will sync automatically.`);
        onOpenChange(false);
        return;
      }
      const { data } = await api.post("/payments", payload);
      const p = data.payment;
      if (p.status === "successful") toast.success(`Paid ${peso(p.amount)} via ${p.provider} • ${p.receipt_no}`);
      else if (p.status === "pending") toast.info(`Payment link created: ${window.location.origin}${p.payment_link}`);
      else toast.error(`Payment failed: ${p.gateway_message}`);
      onPaid?.(p);
      onOpenChange(false);
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card" data-testid="pay-dialog">
        <DialogHeader><DialogTitle>Collect payment — <span className="plate">{session?.plate}</span></DialogTitle></DialogHeader>
        <FeeLines fee={fee} />
        <Field label="Payment method"><Sel value={method} onChange={setMethod} options={methods} testid="pay-method" className="w-full" /></Field>
        {method === "cash" && (
          <Field label="Cash exception reason (mandatory)"><Input data-testid="pay-cash-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. GCash outage, customer has no e-wallet" /></Field>
        )}
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          <Checkbox checked={fail} onCheckedChange={(v) => setFail(!!v)} data-testid="pay-simulate-failure" /> Simulate gateway decline (testing)
        </label>
        <p className="text-[11px] text-muted-foreground">SIMULATED gateway. Card details are never stored by ParkingCore.</p>
        <Button onClick={pay} disabled={busy} data-testid="pay-confirm" className="bg-emerald-600 font-bold hover:bg-emerald-500">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Pay {peso(fee?.balance)}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

export function Receipt({ data }) {
  if (!data) return null;
  const { payment: p, session: s, branch: b } = data;
  return (
    <div className="print-area mx-auto max-w-sm rounded-lg border bg-white p-5 font-mono text-xs text-slate-900" data-testid="receipt">
      <div className="text-center">
        <div className="text-base font-black">PARKINGCORE</div>
        <div>{b.name}</div>
        <div className="text-[10px]">{b.address}</div>
        {b.tin && <div className="text-[10px]">TIN {b.tin}</div>}
        <div className="mt-2 font-bold">DIGITAL RECEIPT</div>
      </div>
      <div className="my-3 border-t border-dashed border-slate-400" />
      {[["Receipt No", p.receipt_no], ["Date/Time", dt(p.created_at, { year: "numeric" })], ["Session", s?.session_no], ["Plate", s?.plate], ["Entry", dt(s?.entry_time)],
        ["Method", label(p.method)], ["Transaction ID", p.transaction_id], ["Status", label(p.status)]].map(([k, v]) => (
        <div key={k} className="flex justify-between gap-2"><span>{k}</span><span className="text-right font-bold">{v || "—"}</span></div>
      ))}
      <div className="my-3 border-t border-dashed border-slate-400" />
      <div className="flex justify-between text-sm font-black"><span>AMOUNT PAID</span><span>{peso(p.amount)}</span></div>
      {p.offline && <div className="mt-2 text-center font-bold">** OFFLINE TRANSACTION (SYNCED) **</div>}
      <div className="mt-3 text-center text-[10px]">Please exit within 15 minutes. ParkingCore.mistermicpos.co</div>
    </div>
  );
}

export function ReceiptDialog({ data, onOpenChange }) {
  return (
    <Dialog open={!!data} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card" data-testid="receipt-dialog">
        <DialogHeader><DialogTitle>Receipt</DialogTitle></DialogHeader>
        <Receipt data={data} />
        <Button variant="outline" onClick={() => window.print()} data-testid="receipt-print"><Printer className="h-4 w-4" /> Print</Button>
      </DialogContent>
    </Dialog>
  );
}
